import { lazy, Suspense, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthenticationTypes, handleAuthenticationChange, resetState } from 'features/security/securitySlice';
import { ReactTitle } from 'components/ReactTitle';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import SSO from './pages/authentication/SSO';
import Logout from './pages/authentication/Logout';
import LoadingSpinner from './pages/global/LoadingSpinner';
import TwoFactorLogin from './pages/authentication/TwoFactorLogin';
import { getBaseName } from 'config/Env';
import './App.scss';
import { Toaster } from 'react-hot-toast';
import { getActiveOrganisation, getRefreshToken, resetTokenData, saveTokenData } from 'api/AuthApi';
import { NumberParam, QueryParamProvider, useQueryParam } from 'use-query-params';
import { ReactRouter5Adapter } from 'use-query-params/adapters/react-router-5';
import queryString from 'query-string';
import { Welcome } from 'pages/welcome/Welcome';
import { useLoginUsingRefreshTokenQuery } from 'features/security/authApi';
import { Favicon } from 'components/Favicon';
import { LoginRouter } from 'pages/authentication/LoginRouter';
import { ROUTES } from 'config/routes';
import { isEmpty } from 'helpers/StringHelper';
import { Embed } from 'pages/public/Embed';
import { Share } from 'pages/public/Share';

export default function App() {
    const baseName = getBaseName();

    return (
        <>
            <ReactTitle title="" />
            <Favicon />
            <Toaster />

            <Router basename={baseName}>
                <QueryParamProvider
                    adapter={ReactRouter5Adapter}
                    options={{
                        searchStringToObject: queryString.parse,
                        objectToSearchString: queryString.stringify,
                    }}
                >
                    <Switch>
                        <Route path={ROUTES.security.checkSsoToken}>
                            <SSO />
                        </Route>
                        <Route path={ROUTES.security.logout}>
                            <Logout />
                        </Route>
                        <Route path={ROUTES.welcome.base} render={() => <Welcome />} />
                        <Route path={ROUTES.public.embed} render={() => <Embed />} />
                        <Route path={ROUTES.public.share} render={() => <Share />} />
                        <Route>
                            <Main />
                        </Route>
                    </Switch>
                </QueryParamProvider>
            </Router>
        </>
    );
}

const AppRouter = lazy(() => import('./router/AppRouter'));

function Main() {
    const { authentication } = useSelector((state) => state.security);

    if (authentication === AuthenticationTypes.FULL) {
        return (
            <Suspense>
                <AppRouter />
            </Suspense>
        );
    }

    if (authentication === AuthenticationTypes.NONE) {
        return <LoginRouter />;
    }

    if (authentication === AuthenticationTypes.PARTIAL) {
        return <TwoFactorLogin />;
    }

    return <AuthenticationUnknown />;
}

function AuthenticationUnknown() {
    const dispatch = useDispatch();
    const { authentication } = useSelector((state) => state.security);
    const refreshToken = getRefreshToken();
    const organisationId = useGetOrganisationId();

    useEffect(() => {
        if (authentication === AuthenticationTypes.UNKNOWN && isEmpty(refreshToken)) {
            resetTokenData();
            dispatch(resetState());
        }
    }, [authentication, refreshToken]);

    if (isEmpty(refreshToken)) {
        return null;
    }

    return <Refresh organisationId={organisationId} refreshToken={refreshToken} />;
}

function Refresh({ organisationId, refreshToken }) {
    const dispatch = useDispatch();

    const refreshResult = useLoginUsingRefreshTokenQuery(
        { refreshToken, organisationId },
        {
            skip: isEmpty(refreshToken),
        },
    );

    useEffect(() => {
        if (refreshResult.isUninitialized) {
            return;
        }

        if (refreshResult.isError) {
            resetTokenData();
            dispatch(resetState());

            return;
        }

        if (refreshResult.isSuccess) {
            const { token, refreshToken } = refreshResult.data;

            // Save in application
            saveTokenData(token, refreshToken)
                .then(() => {
                    dispatch(handleAuthenticationChange({ organisationId }));
                })
                .catch(() => {
                    resetTokenData();
                    dispatch(resetState());
                });
        }
    }, [refreshResult]);

    return (
        <div className="pt-5">
            <LoadingSpinner />
        </div>
    );
}

function useGetOrganisationId() {
    const [forceOrganisationId] = useQueryParam('organisationId', NumberParam);
    const activeOrganisation = getActiveOrganisation();

    if (forceOrganisationId) {
        return forceOrganisationId;
    }

    return activeOrganisation;
}
